import AppConstants from "../../../App/AppConstants";
import { executeCarefully } from "../../../utilities/basicUtilities";
import { convertToMegabytes, extractAudioDuration, readBlobFromURL } from "../../../utilities/fileUtilities";
import { randomString } from "../../../utilities/randomUtilities";
import { AUDIO_UPLOAD_RESTRICTION, MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, VIDEO_RESOLUTIONS } from "./CreatePageConstants";
import { PREVIEW_RESOLUTION } from "./Preview/PreviewDisplayConstants";
import { showDebugInfoForAudioUploadValidation } from "../../../utilities/consoleDebugUtilities";

// The single point to create media source
export function createMediaSource(data, mediaSourceType, mediaId, mediaResourceType) {
    return {
        operationStamp: randomString(),
        data: data,
        mediaSourceType: mediaSourceType,
        mediaId: mediaId,
        mediaResourceType: mediaResourceType
    };
}

// The single point to create media data for videos
export function createMediaSourceDataForVideo(p1080Url, p720Url, p540Url, p360Url) {
    return {
        p1080Url: p1080Url,
        p720Url: p720Url,
        p540Url: p540Url,
        p360Url: p360Url
    };
}

// The single point to create media data for images
export function createMediaSourceDataForImage(url, originalImageUrl = null) {
    return {
        url: url,
        originalImageUrl: originalImageUrl
    };
}

export function createMediaSourceDataByType(url, mediaResourceType) {
    switch (mediaResourceType) {
        case MEDIA_RESOURCE_TYPE.VIDEO: {
            return createMediaSourceDataForVideo(url, url, url, url);
        }
        case MEDIA_RESOURCE_TYPE.IMAGE: {
            return createMediaSourceDataForImage(url);
        }
    }
}

export function getMediaSourceSingleUrl(mediaSource) {
    if (mediaSource) {
        switch (mediaSource.mediaResourceType) {
            case MEDIA_RESOURCE_TYPE.VIDEO: {
                return mediaSource.data.p1080Url;
            }
            case MEDIA_RESOURCE_TYPE.IMAGE: {
                return mediaSource.data.url;
            }
        }
    }

    return '';
}

// Flexible function.
// Returns the necessary object from list according to resolution.
// The last parameter accepts a function to take video height.
// Example: (obj) => obj.height
export function getItemByVideoResolution(items, resolution, getHeightPropertyFunc) {
    if (!resolution)
        resolution = VIDEO_RESOLUTIONS.p1080;

    if (items && items.length) {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            let height = getHeightPropertyFunc(item);

            if (height === resolution.height)
                return item;
        }
    }

    return undefined;
};

export function getMediaResolutionUrl(mediaData, previewResolution) {
    if (previewResolution === PREVIEW_RESOLUTION.SEVEN_TWENTY)
        return mediaData.p720Url;

    if (previewResolution === PREVIEW_RESOLUTION.FOUR_EIGHTY)
        return mediaData.p540Url;

    // PREVIEW_RESOLUTION.THREE_SIXTY and PREVIEW_RESOLUTION.TWO_FORTY,
    return mediaData.p360Url;
}

export const getVideoUrlForResolution = (videoUrls, targetResolution) => {
    let minHeightDifference = Number.MAX_SAFE_INTEGER;
    let closestHeight = 0;

    // Iterate through the array
    for (let videoUrl of videoUrls) {
        // Calculate the absolute difference between the current height and the target resolution
        let heightDifference = Math.abs(videoUrl.height - targetResolution);

        // Update the minimum difference and the closest height if needed
        if (heightDifference < minHeightDifference) {
            minHeightDifference = heightDifference;
            closestHeight = videoUrl.height;
        }
    }

    // Find the first video file that matches the closest height and return its link
    for (let videoUrl of videoUrls) {
        if (videoUrl.height === closestHeight) {
            return videoUrl;
        }
    }
}

// Loads the media urls and creates local references
export function createLocalMediaSourceUrls(mediaSource, onComplete, onError, abortController) {
    switch (mediaSource.mediaResourceType) {
        case MEDIA_RESOURCE_TYPE.VIDEO: {
            if (mediaSource.mediaSourceType === MEDIA_SOURCE_TYPE.VIMEO || mediaSource.mediaSourceType === MEDIA_SOURCE_TYPE.STOCK_VIDEO) {
                readBlobFromURL(mediaSource.data.p720Url, (p720Blob) => {
                    const localUrl = URL.createObjectURL(p720Blob);
                    mediaSource.data.p1080Url = localUrl;
                    mediaSource.data.p720Url = localUrl;
                    mediaSource.data.p540Url = localUrl;
                    mediaSource.data.p360Url = localUrl;

                    executeCarefully(onComplete, mediaSource);
                }, onError, abortController);
            } else {
                executeCarefully(onComplete, mediaSource);
            }
            break;
        }
        case MEDIA_RESOURCE_TYPE.IMAGE: {
            // The implementation can be here. (no need for now)
            executeCarefully(onComplete, mediaSource);
            break;
        }
        default: {
            executeCarefully(onComplete, mediaSource);
            break;
        }
    }
}

export function isVideoSource(mediaSource) {
    if (!mediaSource)
        return false;

    return mediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO;
}

export function isImageSource(mediaSource) {
    if (!mediaSource)
        return false;

    return mediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.IMAGE;
}

export function isColorOrGradientSource(mediaSource) {
    if (!mediaSource)
        return false;

    return mediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.COLOR
        || mediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT;
}

export function getAudioUploadRestrictionForUser(userSubscriptionType) {
    return userSubscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE
        ? AUDIO_UPLOAD_RESTRICTION.FREE
        : AUDIO_UPLOAD_RESTRICTION.PREMIUM;
}

export function validateAudioUpload(inputFile, userSubscriptionType) {
    return new Promise((resolve, reject) => {
        const extension = inputFile.name.split('.').pop();
        extractAudioDuration(extension, inputFile).then(duration => {
            showDebugInfoForAudioUploadValidation(inputFile, duration);

            // Get the validation data for premium or free user.
            const audioUploadRestriction = getAudioUploadRestrictionForUser(userSubscriptionType);

            if (duration < audioUploadRestriction.MIN_DURATION) {
                const units = audioUploadRestriction.MAX_DURATION > 1 ? 'seconds' : 'second';
                reject(`The audio file is too short. Please upload files that are ${audioUploadRestriction.MIN_DURATION} ${units} long or more.`);
                return;
            }

            if (duration > audioUploadRestriction.MAX_DURATION) {
                const durationInMinutes = Math.round(audioUploadRestriction.MAX_DURATION / 60);
                const units = durationInMinutes > 1 ? 'minutes' : 'minute';
                reject(`The audio file is too long. Please upload files that are ${durationInMinutes} ${units} long or less.`);
                return;
            }

            const fileSizeInMegabytes = convertToMegabytes(inputFile.size);

            if (fileSizeInMegabytes < audioUploadRestriction.MIN_SIZE) {
                reject(`The audio file is too small. Please upload files that are ${audioUploadRestriction.MIN_SIZE} mb or more.`);
                return;
            }

            if (fileSizeInMegabytes > audioUploadRestriction.MAX_SIZE) {
                reject(`The audio file is too large. Please upload files that are ${audioUploadRestriction.MAX_SIZE} mb or less.`);
                return;
            }

            if (!audioUploadRestriction.AUDIO_FORMATS.includes(inputFile.type)) {
                reject(`Please select an MP3, WAV or AAC file.`);
                return;
            }

            // Audio file is valid here.
            resolve();
        }).catch((error) => {
            console.error(error);
            reject(`Can't detect the audio duration. Try again or use another audio format.`);
        });
    });
}

